<template>
  <div class="textarea-variables-block">
    <div class="flex flex-column gap-2">
      <label v-if="translation">
        {{ getTranslationLabel(t, translation) }}
      </label>
      <div class="p-fluid">
        <Editor
          ref="editorRef"
          :key="editorKey"
          v-model="inputValue"
          editor-style="height: 320px"
          :modules="quillModules"
          @text-change="handleValueChange"
          @load="handleEditorLoaded"
        >
          <template #toolbar>
            <span class="ql-formats">
              <select class="ql-header">
                <option value="1">{{ t('templateBuilder.configs.title1', null) }}</option>
                <option value="2">{{ t('templateBuilder.configs.title2', null) }}</option>
                <option value="3">{{ t('templateBuilder.configs.title3', null) }}</option>
                <option value="4">{{ t('templateBuilder.configs.title4', null) }}</option>
                <option
                  value=""
                  selected
                >{{ t('templateBuilder.configs.normal', null) }}</option>
              </select>
              <select class="ql-font ql-picker">
                <option
                  value=""
                  selected
                >{{ t('templateBuilder.fields.defaultFontFamily', null) }}</option>
                <option value="arial, helvetica, sans-serif">Arial</option>
                <option value="comic sans ms, cursive">Comic Sans MS</option>
                <option value="courier new, courier, monospace">Courier New</option>
                <option value="georgia, serif">Georgia</option>
                <option value="lucida sans unicode, lucida grande, sans-serif">Lucida Sans Unicode</option>
                <option value="tahoma, geneva, sans-serif">Tahoma</option>
                <option value="times new roman, times, serif">Times New Roman</option>
                <option value="trebuchet ms, helvetica, sans-serif">Trebuchet MS</option>
                <option value="verdana, geneva, sans-serif">Verdana</option>
              </select>
              <select class="ql-size ql-picker">
                <option
                  value=""
                  selected
                >{{ t('templateBuilder.fields.defaultFontSize', null) }}</option>
                <option value="8px">8</option>
                <option value="9px">9</option>
                <option value="10px">10</option>
                <option value="11px">11</option>
                <option value="12px">12</option>
                <option value="13px">13</option>
                <option value="14px">14</option>
                <option value="15px">15</option>
                <option value="16px">16</option>
                <option value="18px">18</option>
                <option value="20px">20</option>
                <option value="22px">22</option>
                <option value="24px">24</option>
                <option value="26px">26</option>
                <option value="28px">28</option>
                <option value="30px">30</option>
                <option value="32px">32</option>
                <option value="34px">34</option>
                <option value="36px">36</option>
                <option value="38px">38</option>
                <option value="40px">40</option>
              </select>
            </span>
            <span class="ql-formats">
              <select
                class="ql-lineheight ql-picker"
              >
                <option
                  v-for="(lineHeight, index) in lineHeightList"
                  :key="`lineHeight_${index}`"
                  :value="lineHeight.value ?? lineHeight"
                >{{ lineHeight.label ?? lineHeight }}</option>
              </select>
              <span
                class="ql-custom-button ql-letterspacing"
                @mousedown="saveRangeSelection"
                @click="toggleLetterSpacing"
              >
                <span class="ql-custom-button-icon">
                  <svg
                    height="1em"
                    viewBox="0 0 512 512"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M32 24C32 10.7 42.7 0 56 0S80 10.7 80 24V488c0 13.3-10.7 24-24 24s-24-10.7-24-24V24zM160 144c0-26.5 21.5-48 48-48h96c26.5
                    0 48 21.5 48 48V368c0 26.5-21.5 48-48 48H208c-26.5 0-48-21.5-48-48V144zM456 0c13.3 0 24 10.7 24 24V488c0 13.3-10.7
                    24-24 24s-24-10.7-24-24V24c0-13.3 10.7-24 24-24z"
                    />
                  </svg>
                </span>
              </span>
              <button class="ql-bold" />
              <button class="ql-italic" />
              <button class="ql-underline" />
              <button class="ql-strike" />
              <EmojisButton
                class="ql-custom-button"
                @on-choose-emoji="insertContent"
              />
              <VariablesButton
                :variables-list="variablesList"
                placeholder="T"
                @on-choose-variable="insertContent"
              />
            </span>
            <span class="ql-formats">
              <span
                class="ql-custom-button"
                @click="toggleColorPicker"
              >
                <span
                  class="ql-custom-button-icon"
                >
                  <svg viewBox="0 0 18 18">
                    <line
                      class="ql-color-label ql-stroke ql-transparent"
                      x1="3"
                      x2="15"
                      y1="15"
                      y2="15"
                    />
                    <polyline
                      class="ql-stroke"
                      points="5.5 11 9 3 12.5 11"
                    />
                    <line
                      class="ql-stroke"
                      x1="11.63"
                      x2="6.38"
                      y1="9"
                      y2="9"
                    />
                  </svg>
                </span>
              </span>
              <span
                class="ql-custom-button"
                @click="toggleColorPickerBackground"
              >
                <span
                  class="ql-custom-button-icon"
                >
                  <svg viewBox="0 0 18 18">
                    <g class="ql-fill ql-color-label"> <polygon points="6 6.868 6 6 5 6 5 7 5.942 7 6 6.868" /> <rect
                      height="1"
                      width="1"
                      x="4"
                      y="4"
                    /> <polygon points="6.817 5 6 5 6 6 6.38 6 6.817 5" /> <rect
                      height="1"
                      width="1"
                      x="2"
                      y="6"
                    /> <rect
                      height="1"
                      width="1"
                      x="3"
                      y="5"
                    /> <rect
                      height="1"
                      width="1"
                      x="4"
                      y="7"
                    /> <polygon points="4 11.439 4 11 3 11 3 12 3.755 12 4 11.439" /> <rect
                      height="1"
                      width="1"
                      x="2"
                      y="12"
                    /> <rect
                      height="1"
                      width="1"
                      x="2"
                      y="9"
                    /> <rect
                      height="1"
                      width="1"
                      x="2"
                      y="15"
                    /> <polygon points="4.63 10 4 10 4 11 4.192 11 4.63 10" /> <rect
                      height="1"
                      width="1"
                      x="3"
                      y="8"
                    /> <path d="M10.832,4.2L11,4.582V4H10.708A1.948,1.948,0,0,1,10.832,4.2Z" /> <path
                      d="M7,4.582L7.168,4.2A1.929,1.929,0,0,1,7.292,4H7V4.582Z"
                    /> <path
                      d="M8,13H7.683l-0.351.8a1.933,1.933,0,0,1-.124.2H8V13Z"
                    /> <rect
                      height="1"
                      width="1"
                      x="12"
                      y="2"
                    /> <rect
                      height="1"
                      width="1"
                      x="11"
                      y="3"
                    /> <path d="M9,3H8V3.282A1.985,1.985,0,0,1,9,3Z" /> <rect
                      height="1"
                      width="1"
                      x="2"
                      y="3"
                    /> <rect
                      height="1"
                      width="1"
                      x="6"
                      y="2"
                    /> <rect
                      height="1"
                      width="1"
                      x="3"
                      y="2"
                    /> <rect
                      height="1"
                      width="1"
                      x="5"
                      y="3"
                    /> <rect
                      height="1"
                      width="1"
                      x="9"
                      y="2"
                    /> <rect
                      height="1"
                      width="1"
                      x="15"
                      y="14"
                    /> <polygon
                      points="13.447 10.174 13.469 10.225 13.472 10.232 13.808 11 14 11 14 10 13.37 10 13.447 10.174"
                    /> <rect
                      height="1"
                      width="1"
                      x="13"
                      y="7"
                    /> <rect
                      height="1"
                      width="1"
                      x="15"
                      y="5"
                    /> <rect
                      height="1"
                      width="1"
                      x="14"
                      y="6"
                    /> <rect
                      height="1"
                      width="1"
                      x="15"
                      y="8"
                    /> <rect
                      height="1"
                      width="1"
                      x="14"
                      y="9"
                    /> <path d="M3.775,14H3v1H4V14.314A1.97,1.97,0,0,1,3.775,14Z" /> <rect
                      height="1"
                      width="1"
                      x="14"
                      y="3"
                    /> <polygon points="12 6.868 12 6 11.62 6 12 6.868" /> <rect
                      height="1"
                      width="1"
                      x="15"
                      y="2"
                    /> <rect
                      height="1"
                      width="1"
                      x="12"
                      y="5"
                    /> <rect
                      height="1"
                      width="1"
                      x="13"
                      y="4"
                    /> <polygon points="12.933 9 13 9 13 8 12.495 8 12.933 9" /> <rect
                      height="1"
                      width="1"
                      x="9"
                      y="14"
                    /> <rect
                      height="1"
                      width="1"
                      x="8"
                      y="15"
                    /> <path d="M6,14.926V15H7V14.316A1.993,1.993,0,0,1,6,14.926Z" /> <rect
                      height="1"
                      width="1"
                      x="5"
                      y="15"
                    /> <path d="M10.668,13.8L10.317,13H10v1h0.792A1.947,1.947,0,0,1,10.668,13.8Z" /> <rect
                      height="1"
                      width="1"
                      x="11"
                      y="15"
                    /> <path d="M14.332,12.2a1.99,1.99,0,0,1,.166.8H15V12H14.245Z" /> <rect
                      height="1"
                      width="1"
                      x="14"
                      y="15"
                    /> <rect
                      height="1"
                      width="1"
                      x="15"
                      y="11"
                    /> </g> <polyline
                      class="ql-stroke"
                      points="5.5 13 9 5 12.5 13"
                    /> <line
                      class="ql-stroke"
                      x1="11.63"
                      x2="6.38"
                      y1="11"
                      y2="11"
                    /> </svg>
                </span>
              </span>

              <button
                class="ql-list"
                value="ordered"
              />
              <button
                class="ql-list"
                value="bullet"
              />
              <select class="ql-align">
                <option
                  value="left"
                  selected
                />
                <option value="center" />
                <option value="right" />
                <option value="justify" />
              </select>
            </span>
            <span class="ql-formats">
              <button class="ql-link" />
              <button class="ql-code-block" />
              <button class="ql-clean" />
            </span>
          </template>
        </Editor>
        <OverlayPanel
          ref="overlayPanelRef"
        >
          <CustomColorPicker
            data-test-id="color-picker"
            color="#000000"
            :color-format="colorFormat"
            title="Color Picker"
            @color-changed="changeColor"
          />
        </OverlayPanel>
        <OverlayPanel
          ref="overlayPanelBackgroundRef"
        >
          <CustomColorPicker
            color="#000000"
            :color-format="colorFormat"
            title="Color Picker"
            @color-changed="changeColorBackground"
          />
        </OverlayPanel>
        <OverlayPanel
          ref="overlayPanelLetterSpacingRef"
        >
          <BaseInputNumber
            v-model="letterSpacingValue"
            suffix="px"
            @update:model-value="changeLetterSpacing"
          />
        </OverlayPanel>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// eslint-disable-next-line max-classes-per-file
import {
  defineComponent, onBeforeMount,
  onMounted,
  onBeforeUnmount,
  onUpdated,
  PropType,
  Ref,
  ref,
  SetupContext,
} from 'vue';

import { useI18n } from 'vue-i18n';

import Editor from 'primevue/editor';
import EmojisButton from '@/components/fields/partials/EmojisButton.vue';
import VariablesButton from '@/components/fields/partials/VariablesButton.vue';
import Quill, { registerImportTemplateSpecialsRegistering } from '@/components/template-builder/utils/quill-setup';
import CustomColorPicker from '@/components/fields/partials/CustomColorPicker.vue';
import OverlayPanel from 'primevue/overlaypanel';

import { ColorFormatEnum, Translation } from '@/types';

// eslint-disable-next-line import/no-cycle
import {
  getTemplateIframeDocument,
  getTranslationLabel,
} from '@/components/template-builder/utils/helpers';

import { TemplateEditorState as state } from '@/composables/template-editor/TemplateEditor';
import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';
import { getLineHeightOptions } from '@/configs/line-height';
import { useEditorBackground } from '@/composables/useEditorBackground';
import { Variable } from '../template-builder/utils/variables-list';

export default defineComponent({
  name: 'BaseWysiwygEditor',
  components: {
    BaseInputNumber,
    OverlayPanel,
    CustomColorPicker,
    VariablesButton,
    EmojisButton,
    Editor,
  },

  props: {
    modelValue: {
      type: String,
      required: true,
    },

    variablesList: {
      type: Array as PropType<Variable[]>,
      required: false,
      default: () => [],
    },

    translation: {
      type: Object as PropType<Translation>,
      required: false,
      default: null,
    },

  },

  emits: {
    'update:modelValue': String,
    'activate-group': String,
    'deactivate-group': String,
  },

  setup(props, { emit }: SetupContext) {
    const { t } = useI18n();

    const overlayPanelLetterSpacingRef = ref();
    const lineHeightList = ref(getLineHeightOptions(t).unshift({
      label: t('templateBuilder.fields.defaultLineHeight'),
      value: '',
    }));
    const letterSpacingValue = ref();
    const selectedRange = ref();
    const editorKey = ref(0);
    const inputValue: Ref<string> = ref('');
    const editorRef = ref();
    const overlayPanelRef = ref();
    const overlayPanelBackgroundRef = ref();
    const colorFormat: Ref<string> = ref(ColorFormatEnum.HEX);
    const { registerEditor, unregisterEditor } = useEditorBackground();

    const cleanTrailingEmptyBlock = (html: string): string => html.replace(/<p><br><\/p>\s*$/, '');

    const valueChangeTimeout = ref<number | null>(null);

    const doValueChange = () => {
      let content = editorRef.value.quill.root.innerHTML;
      content = cleanTrailingEmptyBlock(content);
      inputValue.value = content;
      emit('update:modelValue', content);
    };

    const handleValueChange = () => {
      if (valueChangeTimeout.value) {
        window.clearTimeout(valueChangeTimeout.value);
      }
      valueChangeTimeout.value = window.setTimeout(() => {
        doValueChange();
        valueChangeTimeout.value = null;
      }, 150);
    };

    const insertContent = (value: string | number | undefined) => {
      if (editorRef.value && editorRef.value.quill) {
        editorRef.value.quill.insertText(editorRef.value.quill.selection.savedRange.index, value);
        handleValueChange();
      }
    };

    const toggleColorPicker = (event: MouseEvent) => {
      overlayPanelRef.value.toggle(event);
    };

    const toggleColorPickerBackground = (event: MouseEvent) => {
      overlayPanelBackgroundRef.value.toggle(event);
    };

    const changeColor = (color: string) => {
      editorRef.value.quill.format('color', color);
      handleValueChange();
    };

    const changeColorBackground = (color: string) => {
      editorRef.value.quill.format('background-color', color);
      handleValueChange();
    };

    const saveRangeSelection = (event: MouseEvent) => {
      // Store selected range (because when opening the panel, the range disappears)
      const range = editorRef.value.quill.getSelection();
      if (range !== null) {
        selectedRange.value = range;
      }
    };
    const toggleLetterSpacing = (event: MouseEvent) => {
      letterSpacingValue.value = null;
      overlayPanelLetterSpacingRef.value.toggle(event);
      editorRef.value.quill.setSelection(selectedRange.value);
    };

    const changeLetterSpacing = (value: any) => {
      if (selectedRange.value) {
        editorRef.value.quill.setSelection(selectedRange.value);
        editorRef.value.quill.formatText(selectedRange.value.index, selectedRange.value.length, {
          'letter-spacing': `${value}px`,
        });
        handleValueChange();
      }
    };

    const getSelectorFromTranslation = () => {
      const keys = props.translation.key.split('.');
      let attributeIdentifier = `data-spmtranslationid${keys.length > 1 ? `-${keys[1]}` : ''}`;
      if (props.translation.fieldType === 'link' && keys.length === 1) {
        attributeIdentifier = 'data-spmtranslationid-href';
      }
      return `spmtranslation[id="LANG_${props.translation.key}"], [${attributeIdentifier}="LANG_${props.translation.key}"]`;
    };

    onBeforeMount(async () => {
      editorKey.value += 1;

      const el = editorRef.value?.quill?.root;
      if (el) unregisterEditor(el);
    });

    const checkInterval = ref();

    onMounted(async () => {
      if (state.template?.informations.imported) {
        registerImportTemplateSpecialsRegistering();
      }
      if (editorRef.value && editorRef.value.quill) {
        editorRef.value.quill.disable();
      }

      inputValue.value = props.modelValue;

      // Avoids having an empty history point
      checkInterval.value = window.setInterval(() => {
        if (editorRef.value && editorRef.value.quill) {
          editorRef.value.quill.history.clear();
          window.clearInterval(checkInterval.value);
        }
      }, 200);

      const interval = setInterval(() => {
        const quillInstance = editorRef.value?.quill;
        if (quillInstance?.root) {
          const selector = getSelectorFromTranslation();
          const template = getTemplateIframeDocument();
          const refEl = template.querySelector(selector) as HTMLElement;
          registerEditor(editorRef.value?.quill?.root, refEl);
          const { applyBackgroundReferenceFromSelector } = useEditorBackground();

          applyBackgroundReferenceFromSelector(selector, template);
          clearInterval(interval);
        }
      }, 100);
    });

    onUpdated(() => {
      if (editorRef.value && editorRef.value.quill) {
        editorRef.value.quill.enable();
      }
    });

    const Delta = Quill.import('delta');
    const lineBreakMatcher = () => {
      const newDelta = new Delta();
      newDelta.insert({ break: '' });
      return newDelta;
    };

    // Update Quill behavior when using 'enter' key
    const handleEnter = (range: any) => {
      if (!state.template?.informations.imported) {
        return true; // laisse Quill faire son comportement par défaut: <p>
      }
      const { quill } = editorRef.value;
      // Remove selection to mimic native behavior
      if (range.length > 0) {
        quill.deleteText(range.index, range.length, Quill.sources.USER);
      }
      const nextCharacter = quill.getText(range.index + 1, 1);
      // Insert first line break
      quill.insertEmbed(range.index, 'break', true, Quill.sources.USER);
      let cursorDelta = 1;
      // At end-of-line/doc, insert a second break right after the first one
      if (nextCharacter.length === 0) {
        quill.insertEmbed(range.index + 1, 'break', true, Quill.sources.USER);
        cursorDelta = 2;
      }
      quill.setSelection(range.index + cursorDelta, Quill.sources.SILENT);
      handleValueChange();
      return false; // prevent Quill default (<p>) insertion
    };

    const quillModules = ref({
      clipboard: {
        matchVisual: false,
      },
      keyboard: {
        bindings: {
          // Enter => <br>
          enterBr: {
            key: 13,
            shiftKey: false,
            handler: handleEnter,
          },
          // Shift+Enter => <br> as well
          shiftEnterBr: {
            key: 13,
            shiftKey: true,
            handler: handleEnter,
          },
        },
      },
    });

    const focus = () => {
      if (editorRef.value && editorRef.value.quill) {
        editorRef.value.quill.blur();
        editorRef.value.quill.focus();
      }
    };

    const handleQuillEditorBlur = () => {
      if (props.translation) {
        emit('deactivate-group', props.translation.groupKey);
      }
    };

    const handleQuillEditorFocus = () => {
      if (props.translation) {
        emit('activate-group', props.translation.groupKey);
      }
    };

    const handleEditorLoaded = () => {
      editorRef.value.quill.root.addEventListener('blur', handleQuillEditorBlur);
      editorRef.value.quill.root.addEventListener('focus', handleQuillEditorFocus);
    };

    // Nettoyage avant démontage
    onBeforeUnmount(() => {
      // Flush any pending debounced update to avoid losing the latest changes
      if (valueChangeTimeout.value) {
        window.clearTimeout(valueChangeTimeout.value);
        doValueChange();
        valueChangeTimeout.value = null;
      }
      if (editorRef.value) {
        if (editorRef.value.quill) {
          editorRef.value.quill.root.removeEventListener('blur', handleQuillEditorBlur);
          editorRef.value.quill.root.removeEventListener('focus', handleQuillEditorFocus);
          editorRef.value.quill = null;
        }
        editorRef.value = null;
      }
      if (overlayPanelRef.value) {
        overlayPanelRef.value = null;
      }
      if (overlayPanelBackgroundRef.value) {
        overlayPanelBackgroundRef.value = null;
      }
      if (overlayPanelLetterSpacingRef.value) {
        overlayPanelLetterSpacingRef.value = null;
      }
      if (checkInterval.value) {
        window.clearInterval(checkInterval.value);
      }
    });

    return {
      t,
      inputValue,
      editorRef,
      overlayPanelRef,
      overlayPanelBackgroundRef,
      handleValueChange,
      insertContent,
      toggleColorPicker,
      toggleColorPickerBackground,
      changeColor,
      changeColorBackground,
      colorFormat,
      quillModules,
      focus,
      getTranslationLabel,
      handleEditorLoaded,
      lineHeightList,
      letterSpacingValue,
      toggleLetterSpacing,
      saveRangeSelection,
      changeLetterSpacing,
      overlayPanelLetterSpacingRef,
      editorKey,
    };
  },
});
</script>

<style scoped lang="scss">
:deep() .p-togglebutton.p-button.emoji-button,
:deep() .variable-dropdown {
  font-size: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  display: inline-block !important;
  float: left;
  height: 24px;
  padding: 1px 0 !important;
  width: 28px;
  filter: grayscale(100%);
  .p-dropdown-trigger {
    display: none;
  }
  .p-dropdown-label {
    display: inline;
  }
}
</style>

<style lang="scss">
.ql-custom-button {
  width: 28px!important;
  color: #444;
  display: inline-block!important;
  float: left;
  font-size: 14px;
  font-weight: 500;
  height: 24px!important;
  position: relative;
  vertical-align: middle;
  cursor: pointer;
}

.ql-custom-button .ql-custom-button-icon {
  padding: 2px 4px!important;
  display: inline-block;
  width: 100%!important;
  height: 100%!important;
}
.ql-custom-button .ql-custom-button-icon.fa-microchip-ai {
  vertical-align: top;
  font-size:18px;
}

.p-editor-container .ql-snow.ql-toolbar button.ql-active,
.p-editor-container .ql-snow.ql-toolbar .ql-picker-label.ql-active,
.p-editor-container .ql-snow.ql-toolbar .ql-picker-item.ql-selected  {
  color: $brand-color-primary;
}

.p-editor-container .ql-snow.ql-toolbar button.ql-active .ql-stroke,
.p-editor-container .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke,
.p-editor-container .ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke {
  stroke: $brand-color-primary;
}

.ql-snow .ql-formats .ql-picker.ql-header {
  width: 80px;
}

.ql-snow .ql-formats .ql-picker.ql-font {
  width: 165px;
}

.ql-snow .ql-formats .ql-picker.ql-size {
  width: 75px;
}

.ql-snow .ql-formats .ql-picker.ql-lineheight {
  width: 25px;
  padding: 0px 5px;

  .ql-picker-label {
    padding-left: 0 !important;

    &:after {
      color: #6c757d !important;
      content: '\f871';
      font-family: 'Font Awesome 6 Pro';
      position: absolute;
      top: 4px !important;
      right: 0;
    }

    & > svg {
      display: none;
    }
  }

  .ql-picker-item:before {
    content: attr(data-label);
  }
}

.ql-snow .ql-formats .ql-custom-button.ql-letterspacing {
  width: 20px;
  padding: 3px 0px;
  position: relative;

  & svg {
    fill: #6c757d;
  }
}
</style>
